// =============================================================
// debug.ts — 지갑 연결 진단 오버레이 (운영 상주)
//
//  목적: 모바일에서 지갑 연결이 '어느 층에서' 멈추는지 화면에서 즉시 확인.
//        PC 원격 디버깅(chrome://inspect)이 불가한 사용자 지원용.
//
//  켜는 법: URL 에 ?debug=1        예) https://molebang.com/?debug=1
//           (sessionStorage 에 기억 → 지갑 앱 전환/딥링크 후에도 유지)
//  끄는 법: ?debug=0  또는 오버레이의 [끄기] 버튼. 탭을 닫아도 사라진다.
//
//  ★ 운영 안전성:
//    · ?debug=1 이 없으면 initDebug() 가 즉시 return → 아무 코드도 실행되지 않는다.
//      (console 후킹, DOM 삽입, 이벤트 등록 전부 없음)
//    · dlog()/dumpEnv() 는 enabled=false 면 즉시 return → 오버헤드 0.
//    · 로그는 화면/콘솔에만 남고 외부로 전송되지 않는다.
//    · 지갑 주소가 로그에 남으므로, 사용자에게 로그를 요청할 때는
//      공개 주소(0x…)만 포함된다는 점을 알린다. 개인키/니모닉은 절대 접근하지 않는다.
//
//  사용자 지원 흐름:
//    "연결이 안 돼요" → https://molebang.com/?debug=1 접속 후
//    [지갑 연결] 시도 → 하단 패널의 [복사] → 로그 전달
// =============================================================

const SS_KEY = 'molebang.debug';
const MAX_LINES = 300;

type Level = 'info' | 'ok' | 'warn' | 'err' | 'evt';

interface Line {
  t: number;
  level: Level;
  msg: string;
}

const lines: Line[] = [];
let panel: HTMLElement | null = null;
let body: HTMLElement | null = null;
let enabled = false;
let collapsed = false;
const t0 = Date.now();

const COLLAPSE_KEY = 'molebang.debug.collapsed';

// ── 활성화 판정 ──────────────────────────────────────────────
//  sessionStorage 를 쓰는 이유: 모바일에서 MetaMask 앱으로 갔다 돌아오면
//  페이지가 새로 로드될 수 있는데, 그때 로그가 끊기면 진단이 불가능하다.
//  탭을 닫으면 자동으로 사라지므로 다른 사용자에게 새어나가지 않는다.
function resolveEnabled(): boolean {
  try {
    const q = new URLSearchParams(window.location.search).get('debug');
    if (q === '1') {
      sessionStorage.setItem(SS_KEY, '1');
      return true;
    }
    if (q === '0') {
      sessionStorage.removeItem(SS_KEY);
      sessionStorage.removeItem(COLLAPSE_KEY);
      return false;
    }
    return sessionStorage.getItem(SS_KEY) === '1';
  } catch {
    return false;
  }
}

/** 디버그 모드가 켜져 있는지 (main.ts 에서 조건부 로깅용). */
export function isDebug(): boolean {
  return enabled;
}

/** 패널 접기/펼치기 (모달을 열 때 자동으로 접어 조작을 방해하지 않게 한다). */
export function setDebugCollapsed(v: boolean): void {
  if (!enabled) return;
  collapsed = v;
  applyCollapsed();
  if (!v) render();
}

// ── 로그 ────────────────────────────────────────────────────
export function dlog(msg: string, level: Level = 'info'): void {
  if (!enabled) return;
  lines.push({ t: Date.now() - t0, level, msg });
  if (lines.length > MAX_LINES) lines.shift();
  render();
  // 콘솔에도 남긴다(원격 디버깅 가능한 경우 대비)
  const tag = `[dbg +${((Date.now() - t0) / 1000).toFixed(1)}s]`;
  if (level === 'err') console.error(tag, msg);
  else if (level === 'warn') console.warn(tag, msg);
  else console.info(tag, msg);
}

const COLOR: Record<Level, string> = {
  info: '#9BA3B4',
  ok: '#4ADE80',
  warn: '#FBBF24',
  err: '#F87171',
  evt: '#60A5FA',
};

function render(): void {
  if (!body || collapsed) return;
  body.innerHTML = lines
    .map((l) => {
      const ts = (l.t / 1000).toFixed(1).padStart(5, ' ');
      const safe = l.msg
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return (
        `<div style="display:flex;gap:6px;padding:1px 0">` +
        `<span style="color:#4B5563;flex:0 0 auto">${ts}</span>` +
        `<span style="color:${COLOR[l.level]};word-break:break-all">${safe}</span>` +
        `</div>`
      );
    })
    .join('');
  body.scrollTop = body.scrollHeight;
}

// ── 환경 스냅샷 ─────────────────────────────────────────────
type Eip1193 = {
  request?: (a: { method: string; params?: unknown[] }) => Promise<unknown>;
  isMetaMask?: boolean;
  providers?: unknown[];
  on?: (e: string, h: (...a: unknown[]) => void) => void;
};

function eth(): Eip1193 | undefined {
  return (window as unknown as { ethereum?: Eip1193 }).ethereum;
}

/** 지금 이 순간의 환경을 통째로 찍는다. 멈춤 지점 판별의 핵심. */
export function dumpEnv(tag = 'ENV'): void {
  if (!enabled) return;
  const e = eth();
  dlog(`── ${tag} ──`, 'evt');
  dlog(`url: ${window.location.href}`);
  dlog(`UA: ${navigator.userAgent.slice(0, 90)}`);
  dlog(`window.ethereum: ${e ? 'YES' : 'NO'}`, e ? 'ok' : 'err');
  if (e) {
    dlog(`  .isMetaMask: ${String(e.isMetaMask)}`);
    dlog(`  .providers: ${Array.isArray(e.providers) ? e.providers.length : 'none'}`);
    dlog(`  .request: ${typeof e.request}`);
  }
  try {
    const mm = sessionStorage.getItem('mm_deeplink_launched');
    dlog(`mm_deeplink_launched: ${mm ?? '(none)'}`);
  } catch {
    dlog('sessionStorage: UNAVAILABLE', 'warn');
  }
  // WalletConnect 잔여 세션 흔적
  try {
    const wc = Object.keys(localStorage).filter(
      (k) => k.startsWith('wc@2') || k.startsWith('@appkit') || k.startsWith('@w3m'),
    );
    dlog(`WC storage keys: ${wc.length}${wc.length ? ' → ' + wc.slice(0, 3).join(', ') : ''}`);
  } catch {
    /* noop */
  }
}

/**
 * eth_accounts / eth_requestAccounts 를 직접 때려보고 각 단계를 기록한다.
 * ★ 이게 '멈춤' 의 정체를 밝히는 핵심 프로브.
 *   - eth_accounts 가 즉시 반환되면 → provider 는 살아 있음
 *   - eth_requestAccounts 가 영원히 pending 이면 → MetaMask 응답 채널 끊김(stuck)
 *   - 즉시 reject 되면 → 권한 거부/이미 처리 중
 */
export async function probeWallet(): Promise<void> {
  if (!enabled) return;
  const e = eth();
  if (!e?.request) {
    dlog('probe: window.ethereum.request 없음 → 주입 지갑 아님', 'err');
    return;
  }

  // 1) eth_accounts (팝업 없음, 즉시 응답이어야 정상)
  dlog('probe: eth_accounts 호출…', 'evt');
  const a0 = Date.now();
  try {
    const accs = (await withTimeout(
      e.request({ method: 'eth_accounts' }),
      5000,
    )) as string[];
    dlog(
      `probe: eth_accounts → ${accs?.length ? accs.join(',') : '(빈 배열)'} (${Date.now() - a0}ms)`,
      accs?.length ? 'ok' : 'warn',
    );
    if (accs?.length) {
      dlog('→ 이미 권한 있음. 연결이 안 되면 UI 반영 로직 문제.', 'ok');
      return;
    }
  } catch (err) {
    dlog(`probe: eth_accounts 실패/타임아웃 (${Date.now() - a0}ms): ${errStr(err)}`, 'err');
  }

  // 2) chainId (provider 생존 확인)
  try {
    const cid = await withTimeout(e.request({ method: 'eth_chainId' }), 5000);
    dlog(`probe: eth_chainId → ${String(cid)}`, 'ok');
  } catch (err) {
    dlog(`probe: eth_chainId 실패 → provider 죽음: ${errStr(err)}`, 'err');
  }

  // 3) eth_requestAccounts (팝업 유발). 무응답이면 여기가 범인.
  dlog('probe: eth_requestAccounts 호출… (지갑 팝업이 떠야 정상)', 'evt');
  const a1 = Date.now();
  try {
    const accs = (await withTimeout(
      e.request({ method: 'eth_requestAccounts' }),
      30000,
    )) as string[];
    dlog(`probe: eth_requestAccounts → ${accs?.join(',')} (${Date.now() - a1}ms)`, 'ok');
  } catch (err) {
    const ms = Date.now() - a1;
    if (errStr(err).includes('__timeout__')) {
      dlog(
        `probe: eth_requestAccounts 30초 무응답 → ★ MetaMask 응답 채널 끊김(STUCK) 확정`,
        'err',
      );
      dlog('→ 원인: 인앱 탭이 stale. 딥링크가 새 로드를 못 하고 있음.', 'err');
    } else {
      dlog(`probe: eth_requestAccounts 거부/실패 (${ms}ms): ${errStr(err)}`, 'err');
    }
  }
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, rj) => setTimeout(() => rj(new Error('__timeout__')), ms)),
  ]);
}

function errStr(e: unknown): string {
  const o = e as { code?: number; message?: string; shortMessage?: string };
  return `${o?.code != null ? `[${o.code}] ` : ''}${o?.shortMessage ?? o?.message ?? String(e)}`;
}

// ── UI ──────────────────────────────────────────────────────
function applyCollapsed(): void {
  if (!panel || !body) return;
  body.style.display = collapsed ? 'none' : 'block';
  panel.style.maxHeight = collapsed ? 'none' : '42vh';
  const btn = document.getElementById('dbgFold');
  if (btn) btn.textContent = collapsed ? '▲ 열기' : '▼ 접기';
  const acts = document.getElementById('dbgActions');
  if (acts) acts.style.display = collapsed ? 'none' : 'flex';
  try {
    if (collapsed) sessionStorage.setItem(COLLAPSE_KEY, '1');
    else sessionStorage.removeItem(COLLAPSE_KEY);
  } catch {
    /* noop */
  }
}

function buildPanel(): void {
  try {
    collapsed = sessionStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    collapsed = false;
  }

  panel = document.createElement('div');
  panel.id = 'molebangDebug';
  // ★ z-index 를 낮춘다(9000). 지갑 모달(보통 2147483647 급)보다 아래여야
  //   지갑 목록을 가리지 않는다. 접기 기능과 함께 조작 방해를 없앤다.
  panel.style.cssText =
    'position:fixed;left:0;right:0;bottom:0;z-index:9000;' +
    'background:rgba(8,10,16,.92);border-top:2px solid #D4A843;' +
    'font:11px/1.45 ui-monospace,Menlo,Consolas,monospace;color:#E8E6E0;' +
    'max-height:42vh;display:flex;flex-direction:column';

  const bar = document.createElement('div');
  bar.style.cssText =
    'display:flex;gap:5px;align-items:center;padding:5px 7px;' +
    'border-bottom:1px solid #2A2F3E;flex:0 0 auto;flex-wrap:wrap';
  bar.innerHTML =
    `<button id="dbgFold" style="${BTN}background:#D4A843;color:#0A0C14;flex:0 0 auto">▼ 접기</button>` +
    `<b style="color:#D4A843;flex:1;font-size:10px">MOLEBANG DEBUG</b>` +
    `<span id="dbgActions" style="display:flex;gap:5px">` +
    `<button id="dbgProbe" style="${BTN}">진단</button>` +
    `<button id="dbgEnv" style="${BTN}">ENV</button>` +
    `<button id="dbgCopy" style="${BTN}">복사</button>` +
    `<button id="dbgClear" style="${BTN}">비우기</button>` +
    `<button id="dbgOff" style="${BTN}color:#F87171">끄기</button>` +
    `</span>`;

  body = document.createElement('div');
  body.style.cssText =
    'overflow-y:auto;padding:6px 8px;flex:1 1 auto;-webkit-overflow-scrolling:touch';

  panel.appendChild(bar);
  panel.appendChild(body);
  document.body.appendChild(panel);

  document.getElementById('dbgFold')?.addEventListener('click', () => {
    collapsed = !collapsed;
    applyCollapsed();
  });
  document.getElementById('dbgProbe')?.addEventListener('click', () => void probeWallet());
  document.getElementById('dbgEnv')?.addEventListener('click', () => dumpEnv('ENV (수동)'));
  document.getElementById('dbgClear')?.addEventListener('click', () => {
    lines.length = 0;
    render();
  });
  document.getElementById('dbgOff')?.addEventListener('click', () => {
    disableDebug();
  });
  document.getElementById('dbgCopy')?.addEventListener('click', () => {
    const text = lines.map((l) => `${(l.t / 1000).toFixed(1)} ${l.msg}`).join('\n');
    void navigator.clipboard?.writeText(text).then(
      () => dlog('로그를 클립보드에 복사했습니다.', 'ok'),
      () => dlog('클립보드 복사 실패 — 스크린샷을 찍어 주세요.', 'warn'),
    );
  });

  applyCollapsed();
}

/**
 * 디버그 모드를 끄고 흔적을 지운다.
 *  · URL 에 ?debug=1 이 남아 있으면 새로고침 시 다시 켜지므로 쿼리도 제거한다.
 *    (history.replaceState — 페이지 재로드 없이 주소만 정리)
 */
function disableDebug(): void {
  try {
    sessionStorage.removeItem(SS_KEY);
    sessionStorage.removeItem(COLLAPSE_KEY);
  } catch {
    /* noop */
  }
  try {
    const u = new URL(window.location.href);
    if (u.searchParams.has('debug')) {
      u.searchParams.delete('debug');
      const qs = u.searchParams.toString();
      history.replaceState(null, '', u.pathname + (qs ? '?' + qs : '') + u.hash);
    }
  } catch {
    /* noop */
  }
  panel?.remove();
  panel = null;
  body = null;
  enabled = false;
}

const BTN =
  'padding:4px 7px;font-size:10px;font-weight:700;border:1px solid #3A4155;' +
  'border-radius:5px;background:#161A25;color:#E8E6E0;cursor:pointer;';

// ── 전역 훅 ─────────────────────────────────────────────────
function hookGlobals(): void {
  // ★ console.* 가로채기 — 지갑 어댑터 내부 로그를 오버레이로 끌어온다.
  //   연결 감지 실패는 그 로그에만 흔적이 남는 경우가 있다.
  const origInfo = console.info.bind(console);
  const origWarn = console.warn.bind(console);
  const origErr = console.error.bind(console);
  const relay = (level: Level) => (...args: unknown[]): void => {
    const txt = args
      .map((a) => {
        if (typeof a === 'string') return a;
        try {
          return JSON.stringify(a);
        } catch {
          return String(a);
        }
      })
      .join(' ');
    // 우리 자신의 [dbg] 로그는 되먹임 방지
    if (txt.startsWith('[dbg')) return;
    if (/\[reown\]|\[wcstore\]|\[wallet\]|\[mm\]|\[connect\]|\[boot\]|\[wagmi\]/.test(txt)) {
      lines.push({ t: Date.now() - t0, level, msg: txt.slice(0, 220) });
      if (lines.length > MAX_LINES) lines.shift();
      render();
    }
  };
  console.info = (...a: unknown[]) => {
    relay('info')(...a);
    origInfo(...a);
  };
  console.warn = (...a: unknown[]) => {
    relay('warn')(...a);
    origWarn(...a);
  };
  console.error = (...a: unknown[]) => {
    relay('err')(...a);
    origErr(...a);
  };

  window.addEventListener('error', (e) => {
    dlog(`window.error: ${e.message} @ ${e.filename?.split('/').pop()}:${e.lineno}`, 'err');
  });
  window.addEventListener('unhandledrejection', (e) => {
    dlog(`unhandledrejection: ${errStr(e.reason)}`, 'err');
  });
  document.addEventListener('visibilitychange', () => {
    dlog(`visibility → ${document.visibilityState}`, 'evt');
    if (document.visibilityState === 'visible') dumpEnv('복귀 시점');
  });

  // 지갑 이벤트 직접 구독 (SDK 를 거치지 않고 raw 로)
  const e = eth();
  e?.on?.('accountsChanged', (...a: unknown[]) => {
    dlog(`evt accountsChanged: ${JSON.stringify(a[0])}`, 'evt');
  });
  e?.on?.('chainChanged', (...a: unknown[]) => {
    dlog(`evt chainChanged: ${String(a[0])}`, 'evt');
  });
  e?.on?.('connect', () => dlog('evt connect', 'ok'));
  e?.on?.('disconnect', () => dlog('evt disconnect', 'warn'));
}

/**
 * 숨은 진입점: 지정된 요소를 5회 연속 탭하면 디버그 모드가 켜진다.
 *  · 사용자에게 "URL 뒤에 ?debug=1 붙이세요" 라고 안내하기 어려운 상황용
 *    (특히 모바일 인앱 브라우저는 주소창 편집이 불편하다).
 *
 *  ⚠️ 반드시 전용 요소에만 건다.
 *    이전 구현은 `document.querySelector('footer') ?? document.body` 였는데,
 *    index.html 에 <footer> 가 없어 document.body 로 폴백되었다.
 *    그 결과 페이지 아무 곳이나 0.8초 내 5연타하면 디버그가 켜졌다.
 *    모바일에서 버튼이 안 먹는 줄 알고 연타하는 상황은 흔하므로
 *    일반 사용자에게 오작동으로 노출될 수 있었다.
 */
function armSecretToggle(): void {
  // 전용 트리거 요소만 사용한다. 없으면 아무것도 하지 않는다.
  //  · index.html 의 .trust 문단(맨 아래 고지문)을 트리거로 쓴다.
  //    사용자가 실수로 연타할 일이 거의 없는 정적 텍스트다.
  const foot = document.querySelector('.trust');
  if (!foot) return;

  let hits = 0;
  let last = 0;
  foot.addEventListener('click', () => {
    const now = Date.now();
    hits = now - last < 800 ? hits + 1 : 1;
    last = now;
    if (hits < 5) return;
    hits = 0;
    try {
      sessionStorage.setItem(SS_KEY, '1');
    } catch {
      /* noop */
    }
    location.reload(); // 재부팅해야 console 후킹이 처음부터 걸린다
  });
}

/** 부팅 시 1회 호출. ?debug=1 이 아니면 오버레이를 만들지 않는다. */
export function initDebug(): void {
  enabled = resolveEnabled();

  if (!enabled) {
    // 숨은 진입점만 걸어 두고 종료 (오버헤드 거의 0)
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', armSecretToggle, { once: true });
    } else {
      armSecretToggle();
    }
    return;
  }

  const start = () => {
    buildPanel();
    dlog('디버그 오버레이 시작', 'ok');
    dumpEnv('부팅 시점');
    hookGlobals();
    dlog('▶ [지갑 진단] 을 눌러 어느 단계에서 멈추는지 확인하세요.', 'evt');
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}